// Artwork PDP carousel.
//
// Deliberately tiny and dependency-free: the track is a scroll-snap flex row,
// so dragging, trackpad scrolling, keyboard scrolling and the snap points all
// come from the browser. The only thing script adds is the next control, which
// the design draws as a single forward arrow with no previous, no dots and no
// counter.
//
// The control advances by the NEXT slide's offset rather than a fixed width:
// slide 1 is a square mount and every later slide takes its own aspect ratio,
// so there is no single slide width to step by.
class ArtworkCarouselComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.fairplay-artwork__track');
    this.next = this.querySelector('.fairplay-artwork__next');
    if (!this.track || !this.next) return;

    this.slides = Array.from(this.track.children);
    this.onClick = this.onClick.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.next.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.next?.removeEventListener('click', this.onClick);
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  onClick() {
    // Positions are measured with getBoundingClientRect rather than
    // offsetLeft. The track is position: static, so a slide's offsetParent is
    // the carousel and offsetLeft carries the section's 150px inline padding -
    // which made offsetLeft[0] = 150 against a true scroll offset of 0, so the
    // "first slide past the current position" was always slide 0, the scroll
    // target was inside the current snap zone, and mandatory snap pulled it
    // straight back. The arrow did nothing at all at desktop; it only appeared
    // to work at mobile, where the inset is 0 and the two happened to agree.
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    // Wrap rather than dead-end: the design gives the control no disabled state
    // and there is no previous arrow to get back with. The test has to be
    // "already at the end", not "no slide to the right of here" - the last
    // slide is wider than the track, so there is ALWAYS a slide to the right
    // even when the track cannot scroll any further.
    if (this.track.scrollLeft >= maxScroll - 2) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    const trackLeft = this.track.getBoundingClientRect().left;
    const next = this.slides.find(
      (slide) => slide.getBoundingClientRect().left - trackLeft > 2
    );
    const delta = next ? next.getBoundingClientRect().left - trackLeft : maxScroll;
    this.track.scrollTo({
      left: Math.min(this.track.scrollLeft + delta, maxScroll),
      behavior: 'smooth',
    });
  }

  onScroll() {
    // Nothing to update yet - kept so the scroll listener is the single place
    // any future state (a counter, a disabled end state) would hang off.
  }
}

if (!customElements.get('artwork-carousel-component')) {
  customElements.define('artwork-carousel-component', ArtworkCarouselComponent);
}
